import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, Pencil } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { updateExpenseTypeData, getShowExpenseType } from "@/Api/expenseTypes";
import { selectExpenseCategoriesData } from "@/Api/expenseCategories";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ExpenseTypeProps } from "@/types/expenseType";
import { ExpenseCategoryProps } from "@/types/expenseCategory";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SearchableSelect } from "@/components/ui/searchable-select";

const schema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  description: z.string().optional(),
  expense_category_id: z.number({ message: "*requerido" }),
  is_recurrence: z.boolean().optional(),
});

type SchemaType = z.infer<typeof schema>;

export const UpdateExpenseTypeBtn = ({
  updateFront,
  uuid,
}: {
  updateFront: (data: ExpenseTypeProps) => void;
  uuid: string;
}) => {
  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      expense_category_id: undefined,
      is_recurrence: false,
    },
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState<ExpenseCategoryProps[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (isOpen) {
        setFormState("loading");
        try {
          const { data } = await selectExpenseCategoriesData();
          if (data?.response) {
            setCategories(data.response);
          }

          const { data: responseApi } = await getShowExpenseType(uuid);
          const expenseType = responseApi?.response;

          form.setValue("name", expenseType?.name || "");
          form.setValue("description", expenseType?.description || "");
          form.setValue(
            "expense_category_id",
            expenseType?.expense_category_id
          );
          form.setValue("is_recurrence", expenseType?.is_recurrence || false);

          setFormState("default");
        } catch (error) {
          toast({
            description: "Erro ao carregar dados",
            action: <Check color="#ff0000" />,
          });
          setFormState("error");
          setTimeout(() => {
            setFormState("default");
          }, 1000);
        }
      }
    };

    fetchData();
  }, [isOpen, uuid, form, toast]);

  const handleOpenUpdate = () => {
    setIsOpen(true);
  };

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");
    const payload = {
      name: data.name,
      description: data.description || "",
      is_recurrence: data.is_recurrence || false,
      expense_category_id: data.expense_category_id,
    };

    const { data: apiResponse, ok } = await updateExpenseTypeData(
      uuid,
      payload
    );
    if (!ok) {
      setFormState("error");

      setTimeout(() => {
        setFormState("default");
      }, 1000);
      setIsOpen(false);
      return;
    }

    updateFront(apiResponse?.response);
    toast({
      description: apiResponse?.message || "Atualizado com sucesso!",
      action: <Check color="#1edc2b" />,
    });

    form.reset();

    setFormState("default");
    setIsOpen(false);
  };

  const selectedCategory = categories.find(
    (category) => category.id == form.watch("expense_category_id")
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
          onClick={handleOpenUpdate}
        >
          <Pencil className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Editar Tipo de Despesa
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-full">
          <Form
            onSubmit={form.handleSubmit(formSubmit)}
            {...form}
            className="space-y-3"
          >
            <Form.Row>
              <Form.Field name="name">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Nome <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Input {...field} placeholder="Insira um nome" />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>
            <Form.Row>
              <Form.Field name="description">
                {({ field }) => (
                  <Form.Item>
                    <Form.Label>
                      Descrição <Form.Message />
                    </Form.Label>
                    <Form.Control>
                      <Textarea
                        placeholder="Descrição do tipo de despesa"
                        className="resize-none"
                        {...field}
                        value={field.value || ""}
                      />
                    </Form.Control>
                  </Form.Item>
                )}
              </Form.Field>
            </Form.Row>
            <Form.Row>
              <Form.Field name="expense_category_id">
                {({ field }) => (
                  <Form.Item className="flex flex-col">
                  <Form.Label>
                    Categoria <Form.Message />
                  </Form.Label>
                  <Form.Control>
                    <SearchableSelect
                      items={categories}
                      searchFields={["name"]}
                      displayField="name"
                      placeholder="Buscar categoria..."
                      emptyMessage="Nenhuma categoria encontrada."
                      value={selectedCategory}
                      onSelect={(category) => field.onChange(category.id)}
                      getItemId={(item) => item.id}
                    />
                  </Form.Control>
                </Form.Item>
                )}
              </Form.Field>
            </Form.Row>
            <DialogFooter>
              <Button
                type="submit"
                className={cn("w-full", colors.bg_primary)}
                disabled={formState !== "default"}
              >
                {formState === "loading" && (
                  <>
                    Atualizando <LoaderSpin />
                  </>
                )}
                {formState === "default" && "Atualizar"}
                {formState === "error" && "Erro ao atualizar"}
              </Button>
            </DialogFooter>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
